// src/utils/jwtUtils.js

/**
 * Decode JWT token without verification (client-side only)
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null
 */
export const decodeJWT = (token) => {
  if (!token) return null;
  
  try {
    // Split the token into parts
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('Invalid JWT token format');
      return null;
    }
    
    // Decode the payload (second part)
    const payload = parts[1];
    
    // Base64 decode (handle URL-safe base64)
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
};

/**
 * Extract username from JWT token
 * @param {string} token - JWT token
 * @returns {string|null} Username or null
 */
export const getUsernameFromToken = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded) return null;
  
  // Try common Cognito/JWT username fields
  return decoded.username || 
         decoded.sub || 
         decoded['cognito:username'] || 
         decoded.email || 
         decoded.preferred_username;
};

/**
 * Extract user ID from JWT token
 * @param {string} token - JWT token
 * @returns {string|null} User ID or null
 */
export const getUserIdFromToken = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded) return null;
  
  // Try common user ID fields
  return decoded.sub || // Standard JWT subject
         decoded.user_id || 
         decoded['cognito:username'] || 
         decoded.username;
};

/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if expired
 */
export const isTokenExpired = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;
  
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
};